import * as fs from "node:fs";
import * as os from "node:os";
import * as path from "node:path";
import { execFileSync } from "node:child_process";
import { getGhToken } from "./gh-helpers.js";
import { LocalCliError } from "./errors.js";
import type { ReleaseManifest } from "./release-manifest.js";

const OWNER_REPO = "m8t-labs/m8t";

export interface RepoTree { treeShaOf(p: string): string | undefined }
interface RawTreeEntry { path: string; type: string; sha: string }

export function parseTreeResponse(body: { tree?: RawTreeEntry[] }): RepoTree {
  const map = new Map<string, string>();
  for (const e of body.tree ?? []) if (e.type === "tree") map.set(e.path, e.sha);
  return { treeShaOf: (p) => map.get(p.replace(/\/$/, "")) };
}

export async function fetchRepoTree(
  commit: string,
  deps: { fetchImpl?: typeof fetch; ghToken?: () => Promise<string> } = {},
): Promise<RepoTree> {
  const fetchImpl = deps.fetchImpl ?? fetch;
  const ghToken = deps.ghToken ?? getGhToken;
  let token: string | undefined;
  try { token = await ghToken(); } catch { token = undefined; }
  const url = `https://api.github.com/repos/${OWNER_REPO}/git/trees/${commit}?recursive=1`;
  const res = await fetchImpl(url, {
    headers: { Accept: "application/vnd.github+json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
  if (!res.ok) {
    throw new LocalCliError({ code: "PLATFORM_TREE_FETCH_FAILED", message: `GET ${url} → HTTP ${res.status.toString()}.` });
  }
  return parseTreeResponse((await res.json()) as { tree?: RawTreeEntry[] });
}

export function verifyPersonaTrees(manifest: ReleaseManifest, tree: RepoTree): string[] {
  const out: string[] = [];
  for (const [name, item] of Object.entries(manifest.components.personas.items)) {
    const actual = tree.treeShaOf(item.path);
    if (actual !== undefined && actual !== item.treeSha) {
      out.push(`persona '${name}' (${item.path}): manifest pins ${item.treeSha} but commit tree has ${actual}`);
    }
  }
  return out;
}

/**
 * Resolve a repo-root-shaped directory of release content at manifest.platform.commit.
 * Canonical: one authed tarball fetch → atomic-rename into a per-commit cache.
 * `--content-dir` short-circuits to an operator-supplied checkout (trusted).
 */
export async function resolveReleaseContent(
  manifest: ReleaseManifest,
  opts: { contentDir?: string; cacheRoot?: string; fetchImpl?: typeof fetch; ghToken?: () => Promise<string> } = {},
): Promise<string> {
  if (opts.contentDir) {
    if (!fs.existsSync(path.join(opts.contentDir, "personas"))) {
      throw new LocalCliError({ code: "PLATFORM_CONTENT_DIR_INVALID", message: `--content-dir '${opts.contentDir}' has no personas/ — not a repo checkout.` });
    }
    return opts.contentDir;
  }
  const commit = manifest.platform.commit;
  const cacheRoot = opts.cacheRoot ?? path.join(os.homedir(), ".m8t-stack", "cache");
  const dest = path.join(cacheRoot, `release-${commit}`);
  if (fs.existsSync(path.join(dest, "personas"))) return dest; // cache hit

  const fetchImpl = opts.fetchImpl ?? fetch;
  const ghToken = opts.ghToken ?? getGhToken;
  let token: string | undefined;
  try { token = await ghToken(); } catch { token = undefined; }
  const url = `https://api.github.com/repos/${OWNER_REPO}/tarball/${commit}`;
  const res = await fetchImpl(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
  if (!res.ok) {
    throw new LocalCliError({ code: "PLATFORM_TARBALL_FETCH_FAILED", message: `GET ${url} → HTTP ${res.status.toString()}.` });
  }
  fs.mkdirSync(cacheRoot, { recursive: true });
  const tmp = fs.mkdtempSync(path.join(cacheRoot, `.dl-${commit.slice(0, 8)}-`));
  try {
    const tgz = path.join(tmp, "src.tar.gz");
    fs.writeFileSync(tgz, Buffer.from(await res.arrayBuffer()));
    // Extract, stripping the "m8t-labs-m8t-<sha>/" top-level prefix (tar --strip-components=1).
    const unpacked = path.join(tmp, "unpacked");
    fs.mkdirSync(unpacked);
    extractTarGz(tgz, unpacked); // strips 1 leading component
    // Atomic publish: rename into place (no poisoned partial cache on crash).
    try {
      fs.renameSync(unpacked, dest);
    } catch (e) {
      const err = e as NodeJS.ErrnoException;
      // Only tolerate a benign race against a concurrent writer that already
      // published this exact commit; any other failure must surface.
      const isBenignRace = (err.code === "EEXIST" || err.code === "ENOTEMPTY") && fs.existsSync(path.join(dest, "personas"));
      if (!isBenignRace) {
        throw new LocalCliError({
          code: "PLATFORM_CACHE_PUBLISH_FAILED",
          message: `Could not publish release cache to ${dest}: ${err.message}`,
        });
      }
    }
  } finally {
    fs.rmSync(tmp, { recursive: true, force: true });
  }
  return dest;
}

function extractTarGz(tgz: string, into: string): void {
  // Use the system tar (present on macOS/Linux; Git-for-Windows ships bsdtar) with --strip-components=1.
  // tar's -z flag handles gzip directly — no need to gunzip in JS first.
  execFileSync("tar", ["-xzf", tgz, "-C", into, "--strip-components=1"]);
}
